'use strict';

(function () {

  function safeUrl(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
      const u = new URL(raw.trim(), window.location.href);
      return (u.protocol === 'http:' || u.protocol === 'https:') ? u.href : null;
    } catch (_) { return null; }
  }

  function isValidCountryCode(code) {
    return typeof code === 'string' && /^[a-zA-Z]{2}$/.test(code.trim());
  }

  function makeHoverIntent(onOpen, onClose) {
    let timer = null;
    return {
      open()  { window.clearTimeout(timer); onOpen(); },
      close() { timer = window.setTimeout(onClose, 120); }
    };
  }

  class CustomAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this._slides       = Array.from(this.querySelectorAll('.cab-slides .cab-slide'));
      this._current      = 0;
      this._timer        = null;
      this._cleanupTimer = null;

      if (this._slides.length === 0) return;

      if (this._slides.length === 1) {
        this._slides[0].classList.add('cab-slide--active');
        return;
      }

      const raw = parseInt(this.dataset.autoplay, 10);
      this._speed = isFinite(raw) ? Math.max(2000, Math.min(30000, raw * 1000)) : 4000;

      this._effect = ['fade', 'slide'].includes(this.dataset.effect)
        ? this.dataset.effect : 'fade';

      this._slides.forEach((s, i) => s.classList.toggle('cab-slide--active', i === 0));

      this._bindButtons();

      this._isAutoplay = !this.querySelector('[data-prev],[data-next]');
      if (this._isAutoplay) this._play();
    }

    disconnectedCallback() {
      this._pause();
      window.clearTimeout(this._cleanupTimer);
    }

    _play()  { if (!this._timer) this._timer = window.setInterval(() => this._next(), this._speed); }
    _pause() { window.clearInterval(this._timer); this._timer = null; }
    _next()  { this._goTo((this._current + 1) % this._slides.length); }
    _prev()  { this._goTo((this._current - 1 + this._slides.length) % this._slides.length); }

    _goTo(index) {
      if (index === this._current) return;
      const leaving  = this._slides[this._current];
      const entering = this._slides[index];
      if (!leaving || !entering) return;

      if (this._effect === 'slide') {
        leaving.classList.add('cab-slide--leaving');
        window.clearTimeout(this._cleanupTimer);
        this._cleanupTimer = window.setTimeout(() => {
          leaving.classList.remove('cab-slide--active', 'cab-slide--leaving');
        }, 450);
      } else {
        leaving.classList.remove('cab-slide--active');
      }

      entering.classList.remove('cab-slide--leaving');
      void entering.offsetHeight;
      entering.classList.add('cab-slide--active');
      this._current = index;
    }

    _bindButtons() {
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => { this._pause(); this._prev(); });
      if (next) next.addEventListener('click', () => { this._pause(); this._next(); });

      this.addEventListener('mouseenter', () => { if (this._isAutoplay) this._pause(); });
      this.addEventListener('mouseleave', () => { if (this._isAutoplay) this._play(); });
    }
  }

  if (!customElements.get('custom-announcement-bar')) {
    customElements.define('custom-announcement-bar', CustomAnnouncementBar);
  }

  function initNavDropdowns() {
    document.querySelectorAll('[data-cab-nav]:not([data-cab-init])').forEach(nav => {
      nav.setAttribute('data-cab-init', '');
      const btn      = nav.querySelector('.cab-nav__btn');
      const dropdown = nav.querySelector('.cab-nav__dropdown');
      if (!btn || !dropdown) return;

      const doOpen = () => {
        nav.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        dropdown.removeAttribute('hidden');
      };
      const doClose = () => {
        nav.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        dropdown.setAttribute('hidden', '');
      };

      const { open, close } = makeHoverIntent(doOpen, doClose);

      nav.addEventListener('mouseenter', open);
      nav.addEventListener('mouseleave', close);
      btn.addEventListener('click', e => {
        e.stopPropagation();
        nav.classList.contains('is-open') ? doClose() : doOpen();
      });
      document.addEventListener('click',   e => { if (!nav.contains(e.target)) doClose(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') doClose(); });
    });
  }

  function initLocaleSwitchers() {
    document.querySelectorAll('[data-cab-locale]:not([data-cab-init])').forEach(switcher => {
      switcher.setAttribute('data-cab-init', '');
      const btn      = switcher.querySelector('.cab-locale__btn');
      const dropdown = switcher.querySelector('.cab-locale__dropdown');
      if (!btn || !dropdown) return;

      const currentHost = window.location.hostname.toLowerCase();
      let activeItem = null;

      dropdown.querySelectorAll('.cab-locale__item').forEach(item => {
        const rawCode = (item.dataset.localeCode || '').trim();
        const rawUrl  =  item.dataset.localeUrl  || '';

        if (!isValidCountryCode(rawCode)) {
          item.closest('li')?.remove();
          return;
        }

        try {
          const host = new URL(rawUrl, window.location.href).hostname.toLowerCase();
          if (host === currentHost) {
            item.classList.add('cab-locale__item--current');
            activeItem = item;
          }
        } catch (_) {}

        item.addEventListener('click', e => {
          e.preventDefault();
          const safe = safeUrl(rawUrl);
          if (safe) window.location.href = safe;
        });
      });

      if (activeItem) {
        const srcImg  = activeItem.querySelector('img');
        const btnFlag = btn.querySelector('.cab-locale__cur-flag');
        if (srcImg && btnFlag) {
          btnFlag.src    = srcImg.src;
          btnFlag.srcset = srcImg.srcset || '';
          btnFlag.alt    = srcImg.alt    || '';
        }
      }

      const doOpen = () => {
        btn.setAttribute('aria-expanded', 'true');
        dropdown.removeAttribute('hidden');
      };
      const doClose = () => {
        btn.setAttribute('aria-expanded', 'false');
        dropdown.setAttribute('hidden', '');
      };

      const { open, close } = makeHoverIntent(doOpen, doClose);

      switcher.addEventListener('mouseenter', open);
      switcher.addEventListener('mouseleave', close);
      btn.addEventListener('click', e => {
        e.stopPropagation();
        btn.getAttribute('aria-expanded') === 'true' ? doClose() : doOpen();
      });
      document.addEventListener('click',   e => { if (!switcher.contains(e.target)) doClose(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') doClose(); });
    });
  }

  function initSticky() {
    const bar = document.querySelector('.cab-outer--sticky');
    if (!bar) return;

    const apply = el => {
      if (!el) return;
      el.style.setProperty('position', 'sticky', 'important');
      el.style.setProperty('top',      '0',      'important');
      el.style.setProperty('z-index',  '1000',   'important');
    };

    const wrapper = bar.closest('[id^="shopify-section-"]') || bar.closest('.shopify-section');
    apply(wrapper);

    const section = bar.closest('section');
    if (section && section !== wrapper) apply(section);
  }

  function init() {
    initNavDropdowns();
    initLocaleSwitchers();
    initSticky();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

  document.addEventListener('shopify:section:load', init);

})();